import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { Branding, CompanyProfile, ReportDefinition, ReportResult } from '@goyone/shared';
import { api } from '../../lib/api';
import { closePrintView } from '../../lib/printclose';
import { dateTime } from '../../lib/format';
import { Chart, formatValue } from '../../components/Chart';
import { Logo } from '../../components/Logo';
import { PrintSheet, useDocumentPaper } from '../../components/DocumentPaper';
import '../../styles/print.css';

interface Profile {
  tenant: { name: string };
  branding: Branding;
  company: CompanyProfile;
}

/**
 * التقريرُ مطبوعًا — من عائلة مستندات الشركة نفسها.
 *
 * الترويسةُ والهويةُ هما ترويسةُ الفاتورة وكشفِ الحساب سواء، فما يُطبع أو
 * يُحفظ PDF يبدو صادرًا من الشركة لا من أداةٍ عامّة. والرسمُ نفسُه الذي على
 * الشاشة، ثمّ الجدولُ كاملًا بالإجمالي — الجوابُ حيث لا رسمَ يُقرأ.
 */
export function PrintReport() {
  const [params] = useSearchParams();
  const title = params.get('title') || 'تقرير';
  const definition = useMemo<ReportDefinition | null>(() => {
    try {
      return JSON.parse(params.get('def') ?? '') as ReportDefinition;
    } catch {
      return null;
    }
  }, [params]);

  const profile = useQuery({ queryKey: ['settings'], queryFn: () => api<Profile>('/settings') });
  const result = useQuery({
    queryKey: ['reports', 'run', definition],
    queryFn: () => api<ReportResult>('/reports/run', { method: 'POST', body: definition }),
    enabled: !!definition,
  });
  /* ورقةُ الشركة (ليتر هيد) إن رُفعت: تحلّ محلّ ترويسة النظام وتذييله. */
  const { paper, ready: paperReady } = useDocumentPaper('letterhead');

  if (!definition) {
    return (
      <div className="print-page">
        <p>تعريفُ التقرير غير صالح.</p>
      </div>
    );
  }
  if (profile.isError || result.isError) {
    return (
      <div className="print-page">
        <p>تعذّر تحضير التقرير.</p>
      </div>
    );
  }
  if (!profile.data || !result.data || !paperReady) {
    return (
      <div className="print-page">
        <p>جارٍ التحضير…</p>
      </div>
    );
  }

  const { branding, company, tenant } = profile.data;
  const data = result.data;
  const range =
    definition.from || definition.to
      ? `${definition.from ?? '…'} — ${definition.to ?? '…'}`
      : 'كل المدة';

  return (
    <div
      className="print-root"
      style={{
        ['--doc-primary' as string]: branding.primary,
        ['--doc-ink' as string]: branding.ink,
      }}
    >
      <div className="print-toolbar no-print">
        <button className="btn btn-primary" onClick={() => window.print()}>
          طباعة أو حفظ PDF
        </button>
        <button className="btn" onClick={closePrintView}>
          إغلاق
        </button>
        <span className="small mute">اختر «حفظ كـ PDF» في نافذة الطباعة للحصول على ملف.</span>
      </div>

      <PrintSheet paper={paper}>
        <header className="doc-header">
          <div className="doc-brand">
            {branding.logoUrl ? (
              <img src={branding.logoUrl} alt="" className="doc-logo" />
            ) : (
              <Logo size={52} />
            )}
            <div>
              <div className="doc-company">{company.legalName || tenant.name}</div>
              {branding.documentHeader && (
                <div className="doc-tagline">{branding.documentHeader}</div>
              )}
            </div>
          </div>
          <div className="doc-title-block">
            <div className="doc-kind">تقرير</div>
            <div className="doc-number">{title}</div>
          </div>
        </header>

        <section className="doc-dates">
          <div className="doc-date-row">
            <span className="doc-label">المصدر</span>
            <span>{data.sourceLabel}</span>
          </div>
          <div className="doc-date-row">
            <span className="doc-label">القياس</span>
            <span>
              {data.measureLabel} حسب {data.dimensionLabel}
            </span>
          </div>
          <div className="doc-date-row">
            <span className="doc-label">المدة</span>
            <span className="num">{range}</span>
          </div>
          <div className="doc-date-row">
            <span className="doc-label">أُخرج في</span>
            <span className="num">{dateTime(data.generatedAt)}</span>
          </div>
        </section>

        {definition.chart !== 'TABLE' && data.rows.length > 0 && (
          <section style={{ margin: '16px 0' }}>
            <Chart kind={definition.chart} rows={data.rows} format={data.format} />
          </section>
        )}

        <table className="doc-table">
          <thead>
            <tr>
              <th>{data.dimensionLabel}</th>
              <th className="end">{data.measureLabel}</th>
              <th className="end">النسبة</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.key}>
                <td>{r.label}</td>
                <td className="end num">{formatValue(r.value, data.format)}</td>
                <td className="end num">{r.share}٪</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <strong>الإجمالي</strong>
              </td>
              <td className="end num">
                <strong>{formatValue(data.total, data.format)}</strong>
              </td>
              <td className="end num">100٪</td>
            </tr>
          </tfoot>
        </table>

        <p className="doc-words-text" style={{ marginTop: 12 }}>
          {data.records} سجلًّا داخلًا في التقرير.
        </p>
      </PrintSheet>
    </div>
  );
}
